import { useCallback, useMemo, useRef, type ReactNode } from 'react'
import type { CheckInsBySet } from '../../../hooks/useCheckIns'
import { useFavoriteIdSet } from '../../../contexts/FavoriteFriendsContext'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'
import type { ScheduleFilter } from './scheduleFilter'
import type { PartialRangeEntryPoint, ScheduleSurface, SetInterestEntryPoint } from '../../../lib/analytics'
import type { SetDetailTab } from '../../../components/SetDetailDialog'
import type { OneShotTipDismissReason } from '../../../components/OneShotTip'
import type { SetFeed } from '../../../hooks/useSetPosts'
import type { TotemMarking } from '../../../hooks/useTotemSlots'
import type { SetRangesByUser } from '../../../hooks/useGroupInterests'
import type { Set as FestivalSet, Stage, Artist, PartialRange, UserProfile } from '../../../types'
import { ScheduleFabs } from './ScheduleFabs'
import { SetBlock } from './SetBlock'
import { NowLine } from './NowLine'
import { ScheduleMarkerLines } from './ScheduleMarkerLines'
import { useScheduleMarkersContext } from './scheduleMarkersContext'
import { useTrimConflictTip } from './useTrimConflictTip'
import { SLOT_MIN, SLOT_PX, timeToMinutes } from './timeUtils'
import { DayGridHeaders } from './DayGridHeaders'
import { DayGridTimeAxis } from './DayGridTimeAxis'
import { useDayGridScroll } from './useDayGridScroll'
import { useFabClearance } from './useFabClearance'
import { useFilterTip } from './useFilterTip'

// Stable empty array so set blocks with no interested/going members keep the
// same prop reference across renders — lets React.memo(SetBlock) bail out.
const EMPTY_IDS: string[] = []

export function DayGrid({
  day,
  sets,
  updatedSetIds,
  stages,
  artists,
  interestedSetIds,
  goingSetIds,
  onCycleSet,
  setUserIds,
  setRanges,
  setCheckIns,
  ownRanges,
  onUpdateOwnRange,
  userProfiles,
  currentUserId,
  filter,
  onCycleFilter,
  allStagesWithVisibility,
  hasCustomSettings,
  onReorder,
  onToggleVisibility,
  onResetSettings,
  tipSetId,
  onDismissTip,
  feed,
  showFeedBadge,
  totem,
  onOpenMember,
  highlightSetId,
  onHighlightConsumed,
  timezone,
  festivalMode,
  shareFab,
  onOpenWallpaper,
  eventId,
  surface,
  defaultTab = 'details' as SetDetailTab,
}: {
  day: string
  sets: FestivalSet[]
  /** Sets that moved since the user last opened the schedule. */
  updatedSetIds: ReadonlySet<string>
  stages: Stage[]
  artists: Record<string, Artist>
  interestedSetIds: Set<string>
  goingSetIds: Set<string>
  onCycleSet: (setId: string, entryPoint: SetInterestEntryPoint) => void
  setUserIds: { interested: Record<string, string[]>; going: Record<string, string[]> }
  setRanges?: SetRangesByUser
  setCheckIns?: CheckInsBySet
  ownRanges?: ReadonlyMap<string, PartialRange>
  onUpdateOwnRange?: (setId: string, partialRange: PartialRange | null, entryPoint?: PartialRangeEntryPoint) => void
  userProfiles: Record<string, UserProfile>
  currentUserId?: string | null
  filter: ScheduleFilter
  onCycleFilter: () => void
  allStagesWithVisibility: Array<Stage & { hidden: boolean }>
  hasCustomSettings: boolean
  onReorder: (stageIds: string[]) => void
  onToggleVisibility: (stageId: string) => void
  onResetSettings: () => void
  tipSetId?: string | null
  onDismissTip?: (via: OneShotTipDismissReason) => void
  feed?: SetFeed
  showFeedBadge?: boolean
  totem?: TotemMarking
  onOpenMember?: (uid: string) => void
  highlightSetId?: string | null
  onHighlightConsumed?: () => void
  timezone?: string
  festivalMode?: { eventId: string; startDate: string; endDate?: string | null }
  shareFab?: ReactNode
  /** Own schedule only: opens the lock screen image dialog. */
  onOpenWallpaper?: () => void
  eventId: string
  surface: ScheduleSurface
  defaultTab?: SetDetailTab
}) {
  const favoriteIdSet = useFavoriteIdSet()
  // Busiest set across the whole schedule — the denominator that normalizes each
  // card's crowd border, so the top set always hits the ceiling. Uses the same
  // weighted score as the card (going ×3, interested ×1).
  const crowdMax = useMemo(() => {
    let max = 0
    const ids = new Set([
      ...Object.keys(setUserIds.interested),
      ...Object.keys(setUserIds.going),
    ])
    for (const id of ids) {
      const score =
        (setUserIds.going[id]?.length ?? 0) * 3 + (setUserIds.interested[id]?.length ?? 0)
      if (score > max) max = score
    }
    return max
  }, [setUserIds])
  const liveFiltered = useMemo(() => {
    if (filter === 'none') return sets
    if (filter === 'going') return sets.filter(s => goingSetIds.has(s.id))
    if (filter === 'favorites')
      return sets.filter(
        s =>
          (setUserIds.interested[s.id] ?? []).some(u => favoriteIdSet.has(u)) ||
          (setUserIds.going[s.id] ?? []).some(u => favoriteIdSet.has(u)),
      )
    return sets.filter(s => interestedSetIds.has(s.id) || goingSetIds.has(s.id))
  }, [sets, filter, goingSetIds, interestedSetIds, setUserIds, favoriteIdSet])

  const liveFilteredIds = useMemo(() => new Set(liveFiltered.map(s => s.id)), [liveFiltered])
  const isDimmed = (setId: string) =>
    filter !== 'none' && !liveFilteredIds.has(setId)

  // Festival mode's filter coachmark, only while a tap on the filter would split
  // this day into picks and the rest.
  const filterTip = useFilterTip(
    surface === 'festival_mode' &&
      filter === 'none' &&
      !tipSetId &&
      sets.some(s => interestedSetIds.has(s.id) || goingSetIds.has(s.id)) &&
      sets.some(s => !interestedSetIds.has(s.id) && !goingSetIds.has(s.id)),
  )

  const clashTip = useTrimConflictTip({
    sets,
    goingSetIds,
    ownRanges,
    artists,
    timezone,
    // Never stacked on the first-heart tip — one coachmark at a time.
    enabled: !!onUpdateOwnRange && !tipSetId && !filterTip.open,
  })
  const { noteToggle } = clashTip
  // Only the card's own heart: a toggle from the set dialog would put the tip
  // on a card hidden behind it.
  const cycleSet = useCallback(
    (setId: string, entryPoint: SetInterestEntryPoint) => {
      if (entryPoint === 'card') noteToggle(setId)
      onCycleSet(setId, entryPoint)
    },
    [onCycleSet, noteToggle],
  )

  // Grid time bounds (grid-space minutes), snapped to a row. Hoisted above the
  // early return so the now-line math and the drag hook can share them.
  // Lines outside the sets' range stretch the grid so they land at their real time.
  const cutoff = useDayCutoff()
  const markers = useScheduleMarkersContext()
  const setMinutes = sets.flatMap(s => [timeToMinutes(s.startTime, cutoff), timeToMinutes(s.endTime, cutoff)])
  const hasSets = setMinutes.length > 0
  const allMinutes = hasSets
    ? [...setMinutes, ...[...markers.own, ...markers.shared].map(m => timeToMinutes(m.time, cutoff))]
    : setMinutes
  const gridMin = hasSets ? Math.floor(Math.min(...allMinutes) / SLOT_MIN) * SLOT_MIN : 0
  const gridMax = hasSets ? Math.ceil(Math.max(...allMinutes) / SLOT_MIN) * SLOT_MIN : 0

  const { scrollerRef, gridRef, blockRefs, registerBlock, flashSetId } = useDayGridScroll({
    day,
    sets,
    gridMin,
    gridMax,
    hasSets,
    timezone,
    highlightSetId,
    onHighlightConsumed,
  })

  const stageIdsWithSets = new Set(sets.map(s => s.stageId).filter(Boolean) as string[])
  const dayStages = stages.filter(s => stageIdsWithSets.has(s.id))

  const fabsRef = useRef<HTMLDivElement>(null)
  const { hideFabs, peekShift } = useFabClearance({
    scrollerRef,
    fabsRef,
    blockRefs,
    layoutKey: dayStages.map(s => s.id).join(),
  })

  const totalSlots = (gridMax - gridMin) / SLOT_MIN

  function toRowLine(minutes: number): number {
    return 2 + (minutes - gridMin) / SLOT_MIN
  }

  const gtc = `1rem repeat(${dayStages.length}, minmax(7rem, 1fr)) var(--end-pad)`
  const gtr = `2rem repeat(${totalSlots}, ${SLOT_PX}px)`

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
    <div ref={scrollerRef} className="@container nav-scroll-pad overflow-auto overscroll-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:[scrollbar-width:auto] md:[&::-webkit-scrollbar]:block -mx-4 -mb-4 md:mx-0 md:mb-0 md:border md:rounded-md flex-1 min-h-0">
      <div
        ref={gridRef}
        className="w-full pb-1 [--end-pad:1rem] md:[--end-pad:0px]"
        style={{
          display: 'grid',
          gridTemplateColumns: gtc,
          gridTemplateRows: gtr,
          position: 'relative',
        }}
      >
        <DayGridHeaders dayStages={dayStages} />
        <DayGridTimeAxis gridMin={gridMin} gridMax={gridMax} stageCount={dayStages.length} toRowLine={toRowLine} />

        {/* Set blocks */}
        {sets.map(set => {
          if (!set.stageId) return null
          const colIdx = dayStages.findIndex(s => s.id === set.stageId)
          if (colIdx === -1) return null
          const rowStart = toRowLine(timeToMinutes(set.startTime, cutoff))
          const rowEnd = toRowLine(timeToMinutes(set.endTime, cutoff))
          return (
            <SetBlock
              key={set.id}
              set={set}
              registerBlock={registerBlock}
              highlighted={flashSetId === set.id}
              dimmed={isDimmed(set.id)}
              updated={updatedSetIds.has(set.id)}
              gridColumn={colIdx + 2}
              gridRow={`${rowStart} / ${rowEnd}`}
              artists={artists}
              interestedSetIds={interestedSetIds}
              goingSetIds={goingSetIds}
              onCycleSet={cycleSet}
              interestedMemberIds={setUserIds.interested[set.id] ?? EMPTY_IDS}
              goingMemberIds={setUserIds.going[set.id] ?? EMPTY_IDS}
              crowdMax={crowdMax}
              ownPartialRange={ownRanges?.get(set.id) ?? null}
              memberRanges={setRanges?.[set.id]}
              memberCheckIns={setCheckIns?.[set.id]}
              onUpdateOwnRange={onUpdateOwnRange}
              userProfiles={userProfiles}
              currentUserId={currentUserId}
              showGoingTip={tipSetId === set.id}
              onDismissGoingTip={onDismissTip}
              clashesWith={clashTip.conflicts.get(set.id)}
              clashSpans={clashTip.clashSpans.get(set.id)}
              onClashVisible={clashTip.openingSetId === set.id ? clashTip.onOpeningVisible : undefined}
              showClashTip={clashTip.tipSetId === set.id}
              onDismissClashTip={clashTip.dismiss}
              feed={feed}
              showFeedBadge={showFeedBadge}
              totem={totem}
              onOpenMember={onOpenMember}
              defaultTab={defaultTab}
            />
          )
        })}

        <ScheduleMarkerLines gridRef={gridRef} gridMin={gridMin} gridMax={gridMax} />
        <NowLine gridRef={gridRef} gridMin={gridMin} gridMax={gridMax} day={day} timezone={timezone} />
      </div>
    </div>
    <ScheduleFabs
      filter={filter}
      onCycleFilter={onCycleFilter}
      filterTip={filterTip}
      allStagesWithVisibility={allStagesWithVisibility}
      hasCustomSettings={hasCustomSettings}
      onReorder={onReorder}
      onToggleVisibility={onToggleVisibility}
      onReset={onResetSettings}
      festivalMode={festivalMode}
      shareFab={shareFab}
      onWallpaper={onOpenWallpaper}
      eventId={eventId}
      surface={surface}
      rootRef={fabsRef}
      hidden={hideFabs}
      peekShift={peekShift}
    />
    </div>
  )
}
